// Copy + WhatsApp buttons beside every phone number rendered anywhere in the
// app — tables, drawers, 360 pages, tooltips — without touching the 47 call
// sites that print one. A MutationObserver watches the document; any text
// node carrying an Indian mobile number (10 digits starting 6–9, optional
// +91 / 0 prefix, spaces or dashes) gets a small button pair inserted right
// after it. Idempotent, and never edits React-owned text: the button is a
// sibling node, so a re-render that replaces the text leaves a button whose
// number is gone — the sweep removes those.
//
// Opt out for a subtree with data-no-phone-copy (order ids, AWBs and other
// 10-digit numbers that are not phones).
import { useEffect } from 'react';
import { openWaSend } from './wa/waSend';

const PHONE_RE = /(?:\+?91[\s-]?|\b0)?([6-9]\d{4}[\s-]?\d{5})\b/g;
const SKIP_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT', 'OPTION', 'BUTTON', 'SCRIPT', 'STYLE', 'CODE', 'PRE', 'A', 'SVG', 'svg', 'path']);
const MARK = 'aonePhoneCopy';

function copy(value) {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(value);
  const ta = document.createElement('textarea');
  ta.value = value; ta.style.position = 'fixed'; ta.style.opacity = '0';
  document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
  return Promise.resolve();
}

function makeButtons(digits) {
  const wrap = document.createElement('span');
  wrap.dataset[MARK] = digits;
  wrap.setAttribute('contenteditable', 'false');
  wrap.style.cssText = 'display:inline-flex;gap:2px;vertical-align:middle;margin-left:4px;line-height:1;';
  const mk = (label, title, onClick, href) => {
    const el = document.createElement(href ? 'a' : 'button');
    if (href) { el.href = href; el.target = '_blank'; el.rel = 'noreferrer'; } else el.type = 'button';
    el.title = title; el.textContent = label;
    el.style.cssText = 'border:1px solid rgba(0,0,0,0.14);background:rgba(255,255,255,0.7);border-radius:4px;padding:0 4px;font:600 10px/16px inherit;color:inherit;cursor:pointer;opacity:0.7;text-decoration:none;';
    el.addEventListener('click', (e) => { e.stopPropagation(); if (onClick) { e.preventDefault(); onClick(el); } });
    el.addEventListener('mouseenter', () => { el.style.opacity = '1'; });
    el.addEventListener('mouseleave', () => { el.style.opacity = '0.7'; });
    return el;
  };
  wrap.appendChild(mk('⧉', `Copy ${digits}`, (el) => {
    copy(digits).then(() => { el.textContent = '✓'; setTimeout(() => { el.textContent = '⧉'; }, 1200); }).catch(() => {});
  }));
  // Sends from the brand number through the inbox, never from the staff phone.
  wrap.appendChild(mk('WA', 'Send WhatsApp from AONE', () => openWaSend({ phone: digits, source: 'phone-enhancer' })));
  return wrap;
}

function enhance(root) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(n) {
      if (!n.nodeValue || n.nodeValue.length < 10 || !/[6-9]\d/.test(n.nodeValue)) return NodeFilter.FILTER_REJECT;
      const p = n.parentElement;
      if (!p || SKIP_TAGS.has(p.tagName) || p.closest('[data-no-phone-copy],[contenteditable="true"],[data-aone-phone-copy]')) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const hits = [];
  let n; while ((n = walker.nextNode())) hits.push(n);
  for (const t of hits) {
    PHONE_RE.lastIndex = 0;
    const m = PHONE_RE.exec(t.nodeValue);
    if (!m) continue;
    // Not a phone when the digits continue on either side (part of a longer id).
    const before = t.nodeValue[m.index - 1]; const after = t.nodeValue[m.index + m[0].length];
    if ((before && /\d/.test(before)) || (after && /\d/.test(after))) continue;
    const digits = m[1].replace(/\D/g, '');
    const next = t.nextSibling;
    if (next && next.dataset && next.dataset[MARK] === digits) continue;   // already done
    if (next && next.dataset && next.dataset[MARK]) next.remove();          // number changed
    t.parentNode.insertBefore(makeButtons(digits), t.nextSibling);
  }
}

function sweep() {
  // Buttons whose number left with a re-render.
  document.querySelectorAll(`[data-${MARK.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}]`).forEach((el) => {
    const prev = el.previousSibling;
    if (!prev || prev.nodeType !== 3 || !prev.nodeValue.replace(/\D/g, '').includes(el.dataset[MARK])) el.remove();
  });
}

export default function PhoneCopyEnhancer() {
  useEffect(() => {
    if (typeof window === 'undefined' || !window.MutationObserver) return undefined;
    let timer = null;
    const run = () => { timer = null; try { sweep(); enhance(document.body); } catch { /* never break the page */ } };
    const schedule = () => { if (!timer) timer = setTimeout(run, 150); };
    const obs = new MutationObserver((muts) => {
      // Ignore our own insertions.
      if (muts.every((m) => [...m.addedNodes].every((a) => a.dataset && a.dataset[MARK]) && !m.removedNodes.length && m.type !== 'characterData')) return;
      schedule();
    });
    obs.observe(document.body, { childList: true, subtree: true, characterData: true });
    schedule();
    return () => { obs.disconnect(); if (timer) clearTimeout(timer); };
  }, []);
  return null;
}
